import { useEffect, useState, useCallback } from "react";
import { Heart, Send, Loader2, Sparkles, Quote } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import api from "@/api";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

const TILTS = [-0.7, 0.5, -0.4, 0.7, -0.5, 0.4];

const LivreEsperance = () => {
  const [messages, setMessages] = useState(null);
  const [form, setForm] = useState({ nom: "", message: "" });
  const [loading, setLoading] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/livre-espoir");
      setMessages(data.messages);
    } catch {
      setMessages([]);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/livre-espoir", form);
      toast.success(data.message);
      setMessages((m) => [
        { ...form, id: `local-${Date.now()}`, created_at: new Date().toISOString() },
        ...(m || []),
      ]);
      setForm({ nom: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Vérifiez votre message puis réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base font-medium text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-[#0284C7]";

  return (
    <div data-testid="livre-espoir-page">
      <PageHero
        eyebrow="Livre d'espoir"
        title="Un mot pour eux, une force immense"
        description="Laissez un message de soutien aux personnes atteintes de la maladie de Waldmann et à leurs familles. Quelques mots suffisent à réchauffer une semaine de soins."
        testid="livre-espoir-title"
      />

      <section className="py-16 md:py-24">
        <div className="container-site grid items-start gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          {/* Formulaire */}
          <Reveal>
            <div className="card-site !p-7 sm:!p-9 lg:sticky lg:top-28" data-testid="hope-form-card">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#FFF1F1] text-[#FF6B6B]">
                <Heart size={22} fill="currentColor" />
              </span>
              <h2 className="mt-5 font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0F2C59]">
                Signer le livre d'espoir
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Votre message est publié immédiatement sur le mur. Restez bienveillant : il sera
                lu par des malades et leurs proches. Les liens ne sont pas autorisés.
              </p>
              <form onSubmit={submit} data-testid="hope-form" className="mt-6 space-y-4">
                <div>
                  <label htmlFor="hope-nom" className="mb-1.5 block text-sm font-bold text-slate-700">
                    Votre prénom ou surnom *
                  </label>
                  <input
                    id="hope-nom"
                    required
                    minLength={2}
                    maxLength={60}
                    value={form.nom}
                    onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
                    data-testid="hope-name-input"
                    className={inputCls}
                    placeholder="Ex. : Sophie, une amie de l'association…"
                  />
                </div>
                <div>
                  <label htmlFor="hope-message" className="mb-1.5 block text-sm font-bold text-slate-700">
                    Votre message d'espoir *
                  </label>
                  <textarea
                    id="hope-message"
                    required
                    minLength={5}
                    maxLength={500}
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    data-testid="hope-message-input"
                    className={`${inputCls} resize-y`}
                    placeholder="Quelques mots de courage, de tendresse ou de solidarité…"
                  />
                  <p className="mt-1 text-right text-xs text-slate-400">{form.message.length}/500</p>
                </div>
                <button type="submit" disabled={loading} data-testid="hope-form-submit-button" className="btn-don w-full !py-3.5">
                  {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                  Publier mon message
                </button>
              </form>
            </div>
          </Reveal>

          {/* Mur de messages */}
          <div>
            <Reveal>
              <h2 className="flex items-center gap-3 font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0F2C59]">
                <Sparkles size={22} className="text-[#FF6B6B]" />
                Le mur des messages
              </h2>
            </Reveal>
            {messages === null ? (
              <div className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="hope-wall-loading">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-40 animate-pulse rounded-2xl bg-slate-100" />
                ))}
              </div>
            ) : messages.length === 0 ? (
              <div
                className="mt-8 rounded-2xl border-2 border-dashed border-slate-300 bg-white/60 p-10 text-center"
                data-testid="hope-wall-empty"
              >
                <Heart size={32} className="mx-auto text-[#FF6B6B]" />
                <p className="mt-4 font-heading text-lg font-bold text-[#0F2C59]">
                  Soyez la première personne à écrire sur le mur
                </p>
                <p className="mt-2 text-sm text-slate-500">
                  Votre message d'espoir sera le premier d'une longue chaîne de solidarité.
                </p>
              </div>
            ) : (
              <div className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="hope-wall">
                {messages.map((m, i) => (
                  <Reveal key={m.id} delay={(i % 4) * 0.08}>
                    <article
                      data-testid={`hope-message-${i}`}
                      className="card-site relative h-full !p-6"
                      style={{ transform: `rotate(${TILTS[i % TILTS.length]}deg)` }}
                    >
                      <Quote size={22} className="text-[#FF6B6B]" aria-hidden="true" />
                      <p className="mt-3 text-[15px] italic leading-relaxed text-slate-700">
                        « {m.message} »
                      </p>
                      <footer className="mt-4 flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                        <p className="font-heading text-sm font-bold text-[#0F2C59]">{m.nom}</p>
                        <time className="text-xs font-medium text-slate-400" dateTime={m.created_at}>
                          {fmtDate(m.created_at)}
                        </time>
                      </footer>
                    </article>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LivreEsperance;
